/**
 * Catalog Header Component
 * Logo, announcements button (only for curators), PWA install, user avatar
 * Token-only colors
 */

import { useSafeNavigate } from '@/hooks/useSafeNavigate';
import { toast } from 'sonner';
import { Download, ShieldCheck, Settings } from 'lucide-react';
import { usePWAInstall } from '@/hooks/usePWAInstall';
import { OfflineIndicator } from '@/components/features/OfflineIndicator';
import { useModerationPendingCount } from '@/hooks/useModerationPendingCount';

// Import assets
import logoIcon from '@/assets/logo.png';
import avatarPlaceholder from '@/assets/avatar-placeholder.png';
import iconListBrown from '@/assets/icon-list-brown.png';

interface CatalogHeaderProps {
  avatarUrl?: string;
  hasNotification?: boolean;
  onAvatarPress?: () => void;
  onAnnouncementsPress?: () => void;
  showAnnouncements?: boolean;
  showModeration?: boolean;
  onModerationPress?: () => void;
  showAdmin?: boolean;
  onAdminPress?: () => void;
}

const CatalogHeader = ({
  avatarUrl,
  hasNotification = false,
  onAvatarPress,
  onAnnouncementsPress,
  showAnnouncements = false,
  showModeration = false,
  onModerationPress,
  showAdmin = false,
  onAdminPress,
}: CatalogHeaderProps) => {
  const navigate = useSafeNavigate();
  const { canInstall, promptInstall } = usePWAInstall();
  const { counts, isModerator } = useModerationPendingCount();

  const handleAvatarPress = () => {
    if (onAvatarPress) {
      onAvatarPress();
    } else {
      navigate('/profile');
    }
  };

  const handleAnnouncementsPress = () => {
    if (onAnnouncementsPress) {
      onAnnouncementsPress();
    } else {
      toast('Скоро появится', {
        description: 'Мы уже работаем над этим функционалом!',
      });
    }
  };

  const handleModerationPress = () => {
    if (onModerationPress) {
      onModerationPress();
    } else {
      navigate('/moderation');
    }
  };

  const handleAdminPress = () => {
    if (onAdminPress) {
      onAdminPress();
    } else {
      navigate('/drugadm');
    }
  };

  const handleInstallPWA = async () => {
    const installed = await promptInstall();
    if (installed) {
      toast.success('Приложение установлено!', {
        description: 'Теперь вы можете открыть его с главного экрана',
      });
    }
  };

  // Calculate total pending for badge
  const pendingTotal = counts.total;

  return (
    <header
      data-testid="tab-001-header"
      className="flex items-center justify-between px-4 h-[56px] bg-bg-accent-pink-page"
    >
      {/* Left - Logo */}
      <div className="flex items-center">
        <img
          src={logoIcon}
          alt="Drug"
          className="h-8 w-auto object-contain"
        />
      </div>

      {/* Right - Actions */}
      <div className="flex items-center gap-2">
        {/* Offline indicator */}
        <OfflineIndicator />
        
        {/* Admin button - only for admins */}
        {showAdmin && (
          <button
            type="button"
            onClick={handleAdminPress}
            className="flex items-center justify-center w-10 h-10 rounded-full bg-bg-accent-pink-light"
            aria-label="Администрирование"
          >
            <Settings className="w-5 h-5 text-text-primary" />
          </button>
        )}

        {/* Moderation button - only for moderators */}
        {showModeration && (
          <button
            type="button"
            onClick={handleModerationPress}
            className="relative flex items-center justify-center w-10 h-10 rounded-full bg-bg-accent-pink-light"
            aria-label="Модерация"
          >
            <ShieldCheck className="w-5 h-5 text-text-primary" />
            {/* Pending badge */}
            {pendingTotal > 0 && (
              <span className="absolute -top-1 -right-1 min-w-5 h-5 px-1 flex items-center justify-center text-[10px] font-semibold bg-destructive text-destructive-foreground rounded-full">
                {pendingTotal > 99 ? '99+' : pendingTotal}
              </span>
            )}
          </button>
        )}

        {/* Announcements button - only for curators */}
        {showAnnouncements && (
          <button
            type="button"
            onClick={handleAnnouncementsPress}
            className="flex items-center gap-2 px-4 h-[40px] rounded-button bg-bg-accent-pink-light"
            style={{ fontFamily: '"CoFo Gothic", system-ui, sans-serif' }}
          >
            <img
              src={iconListBrown}
              alt=""
              className="w-5 h-5 object-contain"
            />
            <span className="text-text-primary text-[14px] font-medium">
              Объявления
            </span>
          </button>
        )}

        {/* PWA Install button - only when can install */}
        {canInstall && (
          <button
            type="button"
            onClick={handleInstallPWA}
            className="flex items-center justify-center w-10 h-10 rounded-full bg-bg-accent-pink-light"
            aria-label="Установить приложение"
          >
            <Download className="w-5 h-5 text-text-primary" />
          </button>
        )}

        {/* Avatar */}
        <button
          type="button"
          onClick={handleAvatarPress}
          className="relative w-10 h-10 rounded-full overflow-hidden bg-bg-secondary flex-shrink-0"
        >
          <img
            src={avatarUrl && avatarUrl.trim() !== '' && !avatarUrl.startsWith('blob:') ? avatarUrl : avatarPlaceholder}
            alt="Профиль"
            className="w-full h-full object-cover"
          />
          {/* Notification badge */}
          {hasNotification && (
            <span
              className="absolute top-0 right-0 w-2.5 h-2.5 rounded-full bg-bg-accent-orange border-2 border-bg-accent-pink"
            />
          )}
        </button>
      </div>
    </header>
  );
};

export default CatalogHeader;
